import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingCart, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { PageTitle } from "../components/site/PageTitle";
import { ArtworkUploader } from "../components/site/ArtworkUploader";
import { CustomerFields, emptyCustomer } from "../components/site/CustomerFields";
import { useCart } from "../context/CartContext";
import { api, errMsg, imgUrl } from "../lib/api";

const CartList = ({ items, remove }) => (
  <ul className="space-y-3" data-testid="checkout-cart-list">
    {items.map((it, i) => (
      <li key={it.cid} className="flex items-start justify-between gap-3 rounded-xl border border-white/10 bg-black/40 p-4" data-testid={`checkout-cart-item-${i}`}>
        <div className="text-sm">
          {it.gang && (
            <div className="mb-2 flex items-center gap-3">
              <img src={imgUrl(it.gang.preview_url)} alt="Gang sheet preview" className="h-24 w-auto max-w-[80px] rounded border border-white/10 bg-white object-contain" data-testid={`checkout-item-${i}-gang-preview`} />
              <span className="text-xs text-slate-400">{it.gang.image_count} pictures · ${it.gang.price} / sheet</span>
            </div>
          )}
          {it.designs?.length > 0 && (
            <div className="mb-2 flex gap-2">
              {it.designs.map((d) => <img key={d.preview_path} src={imgUrl(d.preview_url)} alt={`${d.side} preview`} className="h-20 w-20 rounded-lg border border-white/10 object-cover" data-testid={`checkout-item-${i}-preview`} />)}
            </div>
          )}
          <p className="font-display text-xl font-bold uppercase">{it.item_type} <span className="text-[#E5202A]">× {it.quantity}</span></p>
          <p className="text-slate-400">{[it.colors, it.placement, it.sizes && `Sizes: ${it.sizes}`].filter(Boolean).join(" · ") || "No extra details"}</p>
          {it.notes && <p className="mt-1 text-slate-500">{it.notes}</p>}
        </div>
        <button onClick={() => remove(it.cid)} className="rounded-full p-2 text-slate-400 hover:bg-[#E5202A]/20 hover:text-[#E5202A]" data-testid={`checkout-remove-item-${i}`}><Trash2 className="h-4 w-4" /></button>
      </li>
    ))}
  </ul>
);

export default function Checkout() {
  const { items, remove, clear } = useCart();
  const nav = useNavigate();
  const [cust, setCust] = useState(emptyCustomer);
  const [files, setFiles] = useState([]);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { needed_by, ...customer } = cust;
      const payload = { customer, needed_by, notes, files, items: items.map(({ cid, ...rest }) => rest) };
      const { data } = await api.post("/orders", payload);
      clear();
      nav(`/thank-you?n=${data.number}&t=order`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (!items.length) {
    return (
      <div className="pb-24">
        <PageTitle badge="Cart" title="Checkout" testId="checkout-title" />
        <div className="panel mx-auto max-w-lg p-10 text-center" data-testid="checkout-empty">
          <ShoppingCart className="mx-auto h-12 w-12 text-[#E5202A]" />
          <p className="mt-4 text-lg font-semibold">Your cart is empty.</p>
          <Link to="/order" className="btn-red mt-6" data-testid="checkout-start-order-button">Start Custom Order</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-20">
      <PageTitle badge="Cart" title="Checkout" subtitle="Review your items, upload artwork, and submit. We'll send an invoice or payment link before production begins." testId="checkout-title" />
      <form onSubmit={submit} className="mx-auto grid max-w-6xl gap-8 px-6 lg:grid-cols-[1fr_380px]" data-testid="checkout-form">
        <div className="space-y-6">
          <section className="panel space-y-4 p-6 sm:p-8">
            <h2 className="red-title text-2xl">Customer Info</h2>
            <CustomerFields value={cust} onChange={setCust} prefix="checkout" />
          </section>
          <section className="panel space-y-4 p-6 sm:p-8">
            <h2 className="red-title text-2xl">Artwork Upload</h2>
            <ArtworkUploader files={files} onChange={setFiles} testId="checkout-artwork" />
            <div>
              <label className="label">Anything else we should know?</label>
              <textarea className="field min-h-[90px]" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="checkout-notes-input" />
            </div>
          </section>
        </div>
        <aside className="panel h-fit space-y-5 p-6 lg:sticky lg:top-28">
          <div className="flex items-center justify-between">
            <h2 className="red-title text-2xl">Your Cart</h2>
            <Link to="/order" className="text-sm text-[#60A5FA] hover:underline" data-testid="checkout-add-more-link">+ Add item</Link>
          </div>
          <CartList items={items} remove={remove} />
          <button className="btn-red w-full" disabled={busy} data-testid="checkout-submit-button">{busy ? "Submitting..." : "Submit Order for Invoice"}</button>
          <p className="text-xs text-slate-500">No payment is taken now. Full payment is required before production begins.</p>
        </aside>
      </form>
    </div>
  );
}
